// src/pages/finance-portal/fixed-assets/components/fixedAssetFormatters.js
//
// Shared formatters and color maps for the  (Fixed Assets) UI.

export const FA_FONT = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
export const FA_ACCENT = '#7c3aed';        // violet
export const FA_ACCENT_DARK = '#6d28d9';
export const FA_ACCENT_BG = '#f5f3ff';

export function formatCurrency(amount, currency = 'USD') {
    if (amount == null || Number.isNaN(amount)) return '$0.00';
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            maximumFractionDigits: 2,
        }).format(amount);
    } catch (e) {
        return `${currency} ${Number(amount).toFixed(2)}`;
    }
}

export function formatNumber(n, decimals = 0) {
    if (n == null || Number.isNaN(n)) return '0';
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
    }).format(n);
}

export function formatDate(iso) {
    if (!iso) return '\u2014';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '\u2014';
    return d.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
    });
}

export function formatDateTime(iso) {
    if (!iso) return '\u2014';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '\u2014';
    return d.toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
    });
}

export const ASSET_STATUS_COLORS = {
    DRAFT:              { bg: '#fef3c7', color: '#92400e' },
    IN_PROGRESS:        { bg: '#dbeafe', color: '#1e40af' },
    ACTIVE:             { bg: '#dcfce7', color: '#166534' },
    FULLY_DEPRECIATED:  { bg: '#e5e7eb', color: '#374151' },
    IMPAIRED:           { bg: '#fed7aa', color: '#9a3412' },
    HELD_FOR_SALE:      { bg: '#fef3c7', color: '#713f12' },
    DISPOSED:           { bg: '#fecaca', color: '#7f1d1d' },
    TRANSFERRED_OUT:    { bg: '#e5e7eb', color: '#374151' },
};

export const CIP_STATUS_COLORS = {
    PLANNED:               { bg: '#fef3c7', color: '#92400e' },
    IN_PROGRESS:           { bg: '#dbeafe', color: '#1e40af' },
    ON_HOLD:               { bg: '#fed7aa', color: '#9a3412' },
    READY_TO_CAPITALIZE:   { bg: '#ddd6fe', color: '#4c1d95' },
    CAPITALIZED:           { bg: '#dcfce7', color: '#166534' },
    CANCELLED:             { bg: '#fecaca', color: '#7f1d1d' },
};

export const DISPOSAL_STATUS_COLORS = {
    DRAFT:             { bg: '#fef3c7', color: '#92400e' },
    PENDING_APPROVAL:  { bg: '#fed7aa', color: '#9a3412' },
    APPROVED:          { bg: '#dbeafe', color: '#1e40af' },
    POSTED:            { bg: '#dcfce7', color: '#166534' },
    CANCELLED:         { bg: '#fecaca', color: '#7f1d1d' },
};

export const ASSET_CLASS_LABELS = {
    LAND: 'Land',
    BUILDINGS: 'Buildings',
    LEASEHOLD_IMPROVEMENTS: 'Leasehold Improvements',
    MACHINERY_EQUIPMENT: 'Machinery & Equipment',
    VEHICLES: 'Vehicles',
    FURNITURE_FIXTURES: 'Furniture & Fixtures',
    COMPUTER_HARDWARE: 'Computer Hardware',
    COMPUTER_SOFTWARE: 'Computer Software',
    INTANGIBLE_FINITE: 'Intangible (Finite Life)',
    INTANGIBLE_INDEFINITE: 'Intangible (Indefinite Life)',
    OTHER: 'Other',
};

export const DEPRECIATION_METHODS = [
    { value: 'STRAIGHT_LINE', label: 'Straight Line' },
    { value: 'DECLINING_BALANCE_150', label: 'Declining Balance 150%' },
    { value: 'DECLINING_BALANCE_200', label: 'Declining Balance 200%' },
    { value: 'SUM_OF_YEARS_DIGITS', label: 'Sum of Years Digits' },
    { value: 'UNITS_OF_PRODUCTION', label: 'Units of Production' },
    { value: 'MACRS_GDS', label: 'MACRS GDS (Tax)' },
    { value: 'MACRS_ADS', label: 'MACRS ADS (Tax)' },
    { value: 'NONE', label: 'None (Land / Indefinite)' },
];

export const DEPRECIATION_CONVENTIONS = [
    { value: 'FULL_MONTH', label: 'Full Month' },
    { value: 'MID_MONTH', label: 'Mid-Month' },
    { value: 'HALF_YEAR', label: 'Half-Year' },
    { value: 'MID_QUARTER', label: 'Mid-Quarter' },
];

export const DISPOSAL_TYPES = [
    { value: 'SALE', label: 'Sale' },
    { value: 'SCRAP', label: 'Scrap' },
    { value: 'TRADE_IN', label: 'Trade-In' },
    { value: 'THEFT_CASUALTY', label: 'Theft / Casualty' },
    { value: 'DONATION', label: 'Donation' },
    { value: 'ABANDONMENT', label: 'Abandonment' },
    { value: 'INTERNAL_TRANSFER', label: 'Internal Transfer' },
    { value: 'OTHER', label: 'Other' },
];

export function statusChip(status, colorMap) {
    const c = colorMap[status] || { bg: '#e5e7eb', color: '#374151' };
    return { backgroundColor: c.bg, color: c.color };
}

export function humanizeStatus(status) {
    if (!status) return '';
    return String(status)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}
